import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useOrganization } from '../hooks/useOrganization';
import {
  LayoutDashboard, Users, Truck, FileText, Building, UserCheck, Bell,
  BarChart3, HelpCircle, Menu, X, ChevronDown, LogOut, Calendar,
  Package, CreditCard, Settings, User, DollarSign, UserPlus, ChevronRight
} from 'lucide-react';

const menuItems = [
  { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard, permission: 'dashboard.read' },
  { name: 'CRM', path: '/crm', icon: UserPlus, permission: 'crm.read' },
  { name: 'Job Cards', path: '/job-cards', icon: FileText, permission: 'jobcards.read' },
  { name: 'All Job Cards', path: '/all-job-cards', icon: FileText, permission: 'jobcards.read' },
  { name: 'Schedule', path: '/schedule', icon: Calendar, permission: 'schedule.read' },
  { name: 'Service Reminder', path: '/service-reminder', icon: Bell, permission: 'service_reminder.read' },
  { name: 'Inventory', path: '/inventory', icon: Package, permission: 'inventory.read' },
  { name: 'Accounts', path: '/accounts', icon: CreditCard, permission: ['accounts.read', 'invoices.read', 'quotations.read', 'payments.read'] },
  { name: 'Sale Return', path: '/sale-return', icon: CreditCard, permission: 'sale_return.read' },
  { name: 'Reports', path: '/reports', icon: BarChart3, permission: 'reports.read' },
  { name: 'Customers', path: '/customers', icon: UserCheck, permission: 'customers.read' },
  { name: 'Members', path: '/members', icon: Users, permission: 'members.read' },
  { name: 'Vendors', path: '/vendors', icon: Truck, permission: 'vendors.read' },
  { name: 'Branches', path: '/branches', icon: Building, permission: 'branches.read' },
  { name: 'Vehicle Master', path: '/vehicle-master', icon: Truck, permission: 'vehicles.read' },
  { name: 'Settings', path: '/settings', icon: Settings, permission: 'settings.read' },
  { name: 'Support', path: '/support', icon: HelpCircle, permission: 'settings.read' },
];

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const location = useLocation();
  const { user, logout } = useAuth();
  const { hasPermission } = usePermissions();
  const { organization } = useOrganization();
  const { getVisibleModules } = usePermissions();

  React.useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(true);
      else setSidebarOpen(false);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  


  const handleLogout = () => {
    logout();
    setUserDropdownOpen(false);
  };

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">
      {/* Mobile Overlay */}
      {isMobile && sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <div className={`${
        isMobile 
          ? `fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}` 
          : sidebarOpen ? 'w-64' : 'w-16'
      } bg-sidebar-bg text-white transition-all duration-300 flex flex-col`}>
        {/* Company Logo & Branch Name */}
        <div className="p-4 border-b border-gray-700">
          <div className="flex items-center">
            <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center overflow-hidden rounded-full bg-white/10 flex-shrink-0">
              {organization?.logoUrl ? (
                <img src={organization.logoUrl.startsWith('http') ? organization.logoUrl : organization.logoUrl} alt="Logo" className="w-10 h-10 sm:w-14 sm:h-14 object-cover rounded-full" onError={(e) => { e.target.style.display = 'none'; }} />
              ) : (
                <span className="text-white font-bold text-xl sm:text-2xl">🏢</span>
              )}
            </div>
            {(sidebarOpen || isMobile) && (
              <div className="ml-3 overflow-hidden">
                <div className="text-base sm:text-lg font-bold text-white truncate">{organization?.companyName || organization?.company || 'AGMS'}</div>
                {organization?.branchName && (
                  <div className="text-xs sm:text-sm text-gray-300 truncate">{organization.branchName}</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Menu Items */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {getVisibleModules(menuItems).map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          
          return (
            <Link
              key={item.name}
              to={item.path}
              onClick={() => isMobile && setSidebarOpen(false)}
              className={`flex items-center px-4 py-3 text-sm hover:bg-sidebar-hover transition-colors ${
                isActive ? 'bg-sidebar-active border-r-2 border-blue-400' : ''
              }`}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {(sidebarOpen || isMobile) && <span className="ml-3">{item.name}</span>}
            </Link>
          );
          })}
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden w-full">
        {/* Top Navbar */}
        <header className="bg-white shadow-sm border-b border-gray-200 px-3 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-md hover:bg-gray-100 touch-target"
            >
              {isMobile ? <Menu className="w-5 h-5" /> : sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>

            {/* User Dropdown */}
            <div className="relative">
              <button
                onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                className="flex items-center space-x-2 sm:space-x-3 text-gray-700 hover:text-gray-900 touch-target"
              >
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full overflow-hidden bg-gray-300 flex items-center justify-center flex-shrink-0">
                  {user?.avatarUrl || user?.profilePhoto ? (
                    <img
                      src={`http://localhost:5000${user.avatarUrl || user.profilePhoto}`}
                      alt="Profile"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                  ) : null}
                  <span className={`text-xs sm:text-sm font-medium text-gray-600 ${user?.avatarUrl || user?.profilePhoto ? 'hidden' : ''}`}>
                    {user?.name?.charAt(0) || user?.firstName?.charAt(0) || 'U'}
                  </span>
                </div>
                <span className="hidden sm:inline text-sm font-medium truncate max-w-[150px]">
                  {user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`.trim()}
                </span>
                <ChevronDown className="w-4 h-4 hidden sm:inline" />
              </button>

              {userDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border">
                  <Link
                    to="/profile"
                    className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 w-full touch-target"
                    onClick={() => setUserDropdownOpen(false)}
                  >
                    <User className="w-4 h-4 mr-2" />
                    Profile
                  </Link>
                  <button 
                    onClick={handleLogout}
                    className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 w-full text-left touch-target"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}